import { createSlice } from '@reduxjs/toolkit';
import { getPlatformConnections, updateConnectionSettings } from '../api/accounting.api';
import { errorSnackBar } from '../actions/common.action';

const initialState = {
  connections: [],
  selectedConnectionId: null,
  loading: false,
  error: null,
  lastFetchedAt: null,
  lastFetchedAccountId: null
};

// Request deduplication cache reference
let ongoingFetchPromise = null;

const accountingSlice = createSlice({
  name: 'accounting',
  initialState,
  reducers: {
    setConnections: (state, action) => {
      const { connections, accountId } = action.payload || {};
      state.connections = connections || [];
      state.lastFetchedAt = Date.now();
      state.lastFetchedAccountId = accountId || null;
      state.loading = false;
      state.error = null;

      // Auto-select default or first connection if selectedConnectionId is null or invalid
      const exists = state.connections.some(connection => connection.id === state.selectedConnectionId);
      if (!exists && state.connections.length > 0) {
        const defaultConn = state.connections.find(connection => connection.is_default) || state.connections[0];
        state.selectedConnectionId = defaultConn ? defaultConn.id : null;
      }
    },
    setSelectedConnectionId: (state, action) => {
      state.selectedConnectionId = action.payload;
    },
    updateConnectionSettingsSuccess: (state, action) => {
      const { id, settings } = action.payload;
      const index = state.connections.findIndex(connection => connection.id === id);
      if (index !== -1) {
        state.connections[index].settings = settings;
      }
    },
    updateConnectionStatusSuccess: (state, action) => {
      const { id, is_active } = action.payload;
      const index = state.connections.findIndex(connection => connection.id === id);
      if (index !== -1) {
        state.connections[index].is_active = is_active;
      }
    },
    markConnectionExpired: (state, action) => {
      const id = action.payload;
      const index = state.connections.findIndex(connection => connection.id === id);
      if (index !== -1) {
        state.connections[index].is_expired = true;
      }
    },
    clearAccountingState: (state) => {
      state.connections = [];
      state.selectedConnectionId = null;
      state.loading = false;
      state.error = null;
      state.lastFetchedAt = null;
      state.lastFetchedAccountId = null;
      ongoingFetchPromise = null;
    },

    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    }
  }
});

export const {
  setConnections,
  setSelectedConnectionId,
  updateConnectionSettingsSuccess,
  updateConnectionStatusSuccess,
  markConnectionExpired,
  clearAccountingState,
  setLoading,
  setError
} = accountingSlice.actions;

export default accountingSlice.reducer;

/**
 * Fetch platform connections with request deduplication and caching.
 * @param {boolean} forceRefresh - If true, bypasses in-flight promise deduplication.
 * @param {boolean} useOverlay - Controls overlay spinner in API call.
 */
export const fetchAccountingConnections = (forceRefresh = false, useOverlay = true) => async (dispatch, getState) => {
  const { accounting, user } = getState();
  const currentAccountId = user?.userData?.selected_account?.id;

  // 1. Valid Cache Hit: Same account and already fetched once
  if (
    !forceRefresh &&
    accounting.lastFetchedAccountId === currentAccountId &&
    accounting.lastFetchedAt !== null
  ) {
    return accounting.connections;
  }

  // 2. Return ongoing promise if already fetching
  if (ongoingFetchPromise && !forceRefresh) {
    return ongoingFetchPromise;
  }

  if (useOverlay && accounting.connections.length === 0) {
    dispatch(setLoading(true));
  }

  ongoingFetchPromise = (async () => {
    try {
      const connections = await getPlatformConnections(useOverlay);
      const activeConnections = Array.isArray(connections) ? connections.filter(connection => connection.is_active !== false) : [];
      dispatch(setConnections({ connections: activeConnections, accountId: currentAccountId }));
      return activeConnections;
    } catch (err) {
      dispatch(setError(err.message || 'Failed to fetch accounting connections'));
      dispatch(errorSnackBar(err.message || 'Failed to fetch platform connections'));
      throw err;
    } finally {
      ongoingFetchPromise = null;
    }
  })();

  return ongoingFetchPromise;
};


/**
 * Save connection settings to DB and update Redux store atomically.
 */
export const saveAccountingConnectionSettings = (connectionId, settings) => async (dispatch) => {
  try {
    await updateConnectionSettings(connectionId, settings);
    dispatch(updateConnectionSettingsSuccess({ id: connectionId, settings }));
    return settings;
  } catch (err) {
    dispatch(errorSnackBar(err.message || 'Failed to update accounting settings'));
    throw err;
  }
};
